import { QuantificationMethodEnumSchema } from "@domain/schemas/BI";

export type QuantificationOption = {
  label: string;
  value: QuantificationMethodEnumSchema | undefined;
};

export const LABEL_FREE: QuantificationOption = { label: "Label free (no quantification)", value: undefined };

/**
 * Display tokens keyed by the generated enum's wire value.
 *
 * These are the exact strings SpectroMine accepts after -q; they differ from the wire
 * value only in casing and punctuation. They are cosmetic: the value sent to the API is
 * always the enum member, and the backend rebuilds the CLI token from its own
 * Description attribute.
 *
 * Deliberately partial, and deliberately keyed by string rather than by enum member.
 * Membership and ordering of the options come from the generated enum, not from here, so
 * a method added backend-side appears in the list as soon as the schema is regenerated;
 * it simply renders under its raw wire value until a token is added here.
 */
const DISPLAY_TOKENS: Record<string, string> = {
  EasiTag6Plex: "EASI-tag6Plex",
  ITraq4Plex: "iTRAQ4Plex",
  ITraq8Plex: "iTRAQ8Plex",
  Tmt6Plex: "TMT6Plex",
  Tmt10Plex: "TMT10Plex",
  Tmt11Plex: "TMT11Plex",
  TmtPro16: "TMTpro16",
  TmtPro18: "TMTpro18",
};

export const labelForQuantificationMethod = (method: QuantificationMethodEnumSchema): string => DISPLAY_TOKENS[method] ?? method;

const knownOptions = (): QuantificationOption[] => [
  LABEL_FREE,
  ...Object.values(QuantificationMethodEnumSchema).map((method) => ({
    label: labelForQuantificationMethod(method),
    value: method,
  })),
];

/**
 * Builds the selectable options for the current selection.
 *
 * A job can carry a method this build has never heard of, for example when it is rerun
 * after the backend gained a method but before the schema was regenerated. Collapsing it
 * to "Label free" would misreport the job and then silently strip the method on submit,
 * so an unrecognised value is surfaced as its own option and preserved.
 */
export const buildQuantificationOptions = (selectedMethod: QuantificationMethodEnumSchema | undefined): QuantificationOption[] => {
  const options = knownOptions();

  return selectedMethod === undefined || options.some((option) => option.value === selectedMethod)
    ? options
    : [...options, { label: `${selectedMethod} (not supported by this version)`, value: selectedMethod }];
};
